import Documents from "components/Documents/drawer/DocumentsDrawer";
import { useFileDOI } from "components/DOIHeaderSelect/useFileDOI";
import LoadingIcon from "components/Loading/LoadingIcon";
import TaskModal from "pages/tasks/components/modals/TaskModal";
import CodeDirectoryDrawer from "components/CodeDirectory/CodeDirectoryDrawer";
import ClientInformationDrawer from "pages/client/ClientInformationDrawer/ClientInformationDrawer";
import { useCallback, useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { clearCurrentTask } from "reducers/files";
import { setIsLoading, setShowTaskModal } from "reducers/main";
import {
  useGetAmendedReviewByIdQuery,
  usePatchAmendedReviewByIdMutation,
} from "services/ar-screening-api";
import { usePatchClaimantByIdMutation } from "services/claimant-api";
import { usePatchDateOfInjuryByIdMutation } from "services/doi-api";
import { usePatchFileByIdMutation } from "services/file-api.ts";
import { useToken } from "utils/context";
import { useDispatch, useSelector } from "utils/redux";

import FutureMedicalRightPanel from "../shared/FMRightPanel";
import AmendedReviewPageContainer from "./AmendedReviewPageContainer";
import ARSClaimantInformation from "./ARSClaimantInformation";

const AmendedReviewSectionPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { accessToken } = useToken();
  const { reviewId, section } = useParams<{
    reviewId: string;
    section: string;
  }>();
  const showTaskModal = useSelector((state) => state.main.showTaskModal);

  const {
    data: review,
    isLoading,
    isError,
  } = useGetAmendedReviewByIdQuery({ reviewId }, { skip: !reviewId });

  const [patchAmendedReviewByIdMutation] = usePatchAmendedReviewByIdMutation();
  const [patchClaimantById] = usePatchClaimantByIdMutation();
  const [patchFileById] = usePatchFileByIdMutation();
  const [patchDateOfInjuryById] = usePatchDateOfInjuryByIdMutation();

  // Get access to the selected DOI for use in updating DOI-related fields
  const { selectedDOI } = useFileDOI(review?.file);

  const handleClose = useCallback(() => {
    dispatch(clearCurrentTask());
    dispatch(setShowTaskModal(false));
  }, [dispatch]);

  const modal = useMemo(
    () => (
      <TaskModal
        isOpen={showTaskModal}
        onClose={handleClose}
        fileId={review?.file?.id?.toString()}
        serviceId={review?.service}
      />
    ),
    [showTaskModal, handleClose, review],
  );

  const { reset } = useFormContext();
  useEffect(() => {
    if (!isLoading && review) {
      const claimant = review.file?.claimants?.[0];
      const formValues = {
        "claimant.first_name": claimant.first_name || "",
        "claimant.last_name": claimant.last_name || "",
        "claimant.ssn": claimant.ssn || "",
        "claimant.address": claimant.address || "",
        "claimant.state": claimant.state || "",
        "claimant.zip_code": claimant.zip_code || "",
        "claimant.birth_date": claimant.birth_date || "",
        "claimant.gender": claimant.gender || "",
        "claimant.is_medicare_eligible": claimant.is_medicare_eligible || false,
        "claimant.reasonable_expectation":
          claimant.reasonable_expectation || false,

        "file.jurisdiction": review.file?.jurisdiction || "",
        "file.one_eleven_jurisdiction":
          review.file?.one_eleven_jurisdiction || "",
        "file.claim_numbers[0].number":
          review.file?.claim_numbers?.[0]?.number || "",
        "file.dates_of_injury[0].date":
          review.file?.dates_of_injury?.[0]?.date || "",
      };

      reset(formValues);
    }
  }, [review, reset, isLoading]);

  useEffect(() => {
    if (!isLoading && (!review || isError)) {
      navigate("/404");
    }
  }, [review, isLoading, isError, history]);

  useEffect(() => {
    dispatch(setIsLoading(isLoading));
  }, [dispatch, isLoading]);

  const getField = (field: string) =>
    field?.includes(".") ? field.split(".")[1] : field;

  const handleSave = useCallback(
    async (field: string | null, value: unknown) => {
      if (!review?.id || !accessToken) return;

      try {
        if (field?.startsWith("claimant.")) {
          const claimantId = review.file.claimants[0]?.id;
          if (!claimantId) return;

          const fieldName = field.replace("claimant.", "");
          await patchClaimantById({
            id: claimantId.toString(),
            data: { [fieldName]: value },
          }).unwrap();
        } else if (field?.startsWith("date_of_injury.")) {
          if (selectedDOI?.id) {
            await patchDateOfInjuryById({
              id: selectedDOI.id.toString(),
              data: {
                [getField(field)]: value,
              },
            }).unwrap();
          } else {
            console.error(
              "Cannot update DOI: selectedDOI is null or id is missing",
            );
          }
        } else if (field?.startsWith("file.")) {
          const fieldPath = field.replace("file.", "");

          await patchFileById({
            id: review.file.id.toString(),
            data: { [fieldPath]: value },
          }).unwrap();
        } else {
          const data = !field ? value : { [field]: value };
          await patchAmendedReviewByIdMutation({
            id: review.id.toString(),
            data,
          }).unwrap();
        }
      } catch (error) {
        console.error("Error saving:", error);
      }
    },
    [
      review?.id,
      patchAmendedReviewByIdMutation,
      patchClaimantById,
      patchFileById,
      patchDateOfInjuryById,
      selectedDOI,
      getField,
    ],
  );

  if (isLoading) return <LoadingIcon />;
  if (!review) return null;

  return (
    <AmendedReviewPageContainer layout="split" service={review}>
      {modal}
      <ARSClaimantInformation
        onSave={handleSave}
        service={review}
        section={section}
      />
      <FutureMedicalRightPanel
        section={section}
        service={review}
        onSave={handleSave}
      />
      <ClientInformationDrawer service={review} />
      <Documents
        fileId={review?.file?.id}
        serviceId={review?.id}
        serviceType="ars"
      />
      <CodeDirectoryDrawer />
    </AmendedReviewPageContainer>
  );
};

export default AmendedReviewSectionPage;
